<template>
  <lila-default-template>
    <template #mainContent>
      <div
        class="container automatic-messages"
        :class="loadingContent ? 'loading large' : ''"
      >
        <nav class="am-breadcrumb">
          <span>Gestão</span>
          <span class="am-breadcrumb-sep">›</span>
          <span>Configurações</span>
          <span class="am-breadcrumb-sep">›</span>
          <strong>Mensagens Automáticas</strong>
        </nav>

        <h1 class="title">
          Configurar resposta automática
        </h1>

        <p class="am-intro">
          A mensagem será enviada automaticamente quando um paciente entrar em
          contato durante os períodos configurados.
        </p>

        <lila-message :alert-data="alert" />

        <div
          v-show="!loadingContent"
          class="am-layout"
        >
          <aside class="am-card am-list">
            <header class="am-card-head">
              <h2 class="am-card-title">
                Mensagens
              </h2>
              <p class="am-card-subtitle">
                Cada mensagem tem o seu próprio período de envio.
              </p>
            </header>

            <p
              v-if="messages.length === 0"
              class="am-list-empty"
            >
              Nenhuma mensagem automática configurada ainda.
            </p>

            <ul
              v-else
              class="am-list-items"
            >
              <li
                v-for="message in messages"
                :key="message.id"
              >
                <button
                  type="button"
                  class="am-list-item"
                  :class="{ 'is-selected': selectedKey === message.id }"
                  @click="selectMessage(message)"
                >
                  <span class="am-list-item-name">{{ message.title }}</span>

                  <span class="am-list-item-days">
                    <i
                      class="fas"
                      :class="periodIcon(message)"
                      aria-hidden="true"
                    />
                    {{ daysSummary(message.days) }}
                  </span>

                  <span class="am-list-item-time">{{ timeSummary(message) }}</span>

                  <span
                    v-if="unitName(message.unitId)"
                    class="am-list-item-unit"
                  >{{ unitName(message.unitId) }}</span>
                </button>
              </li>
            </ul>

            <button
              type="button"
              class="am-add"
              @click="startNewMessage"
            >
              <i
                class="fas fa-plus"
                aria-hidden="true"
              />
              Adicionar mensagem
            </button>
          </aside>

          <section
            v-if="selectedKey === null"
            class="am-card am-placeholder"
          >
            <i
              class="far fa-comment-dots"
              aria-hidden="true"
            />
            <p>Selecione uma mensagem à esquerda ou adicione uma nova.</p>
          </section>

          <section
            v-else
            class="am-card am-detail"
          >
            <header class="am-card-head am-detail-head">
              <h2 class="am-card-title">
                Mensagem automática
              </h2>

              <button
                v-if="form.id"
                type="button"
                class="am-delete"
                aria-label="Excluir mensagem"
                @click="askDelete"
              >
                <i
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </button>
            </header>

            <div class="am-field">
              <label class="am-label">Nome da mensagem</label>
              <lila-input
                v-model="v.form.title.$model"
                placeholder="Ex.: Fora do horário"
                :validation="v.form.title"
              />
              <p class="am-hint">
                O paciente recebe este nome como primeira linha da mensagem.
              </p>
            </div>

            <div class="am-field">
              <label class="am-label">Mensagem</label>
              <lila-textarea
                v-model="v.form.body.$model"
                placeholder="Escreva a mensagem que o paciente vai receber"
                :validation="v.form.body"
              />
              <p class="am-counter">
                {{ form.body.length }} caracteres
              </p>
            </div>

            <div class="am-tip">
              <i
                class="far fa-lightbulb"
                aria-hidden="true"
              />
              <p>
                <strong>Dica:</strong> seja claro e objetivo na sua mensagem
                automática. Informe quando o paciente pode esperar retorno e, se
                necessário, oriente sobre casos de urgência.
              </p>
            </div>

            <div class="am-field">
              <label class="am-label">Aplicar a</label>
              <div class="am-unit-picker-wrap">
                <button
                  type="button"
                  class="am-unit-select"
                  :disabled="loadingUnits"
                  @click.stop="unitPickerOpen = !unitPickerOpen"
                >
                  <span :class="{ 'is-placeholder': !selectedUnitLabel }">
                    {{ selectedUnitLabel || unitPlaceholder }}
                  </span>
                  <span class="am-unit-caret" />
                </button>
                <div
                  v-if="unitPickerOpen"
                  class="am-unit-picker"
                >
                  <team-tree-picker
                    :tree="unitTree"
                    :immediate="true"
                    :current-id="form.unitId"
                    :show-current="false"
                    :cover-descendants="true"
                    :allow-deselect="true"
                    :all-chosen="form.allInstitution"
                    :all-label="canSelectInstitution ? 'Toda a instituição' : ''"
                    title="Selecionar unidade / equipe"
                    @select="pickUnit"
                    @select-all="pickInstitution"
                    @cancel="unitPickerOpen = false"
                  />
                </div>
              </div>
              <p class="am-hint">
                A mensagem vale para a unidade escolhida e todas as equipes
                abaixo dela.{{ canSelectInstitution ? " Marque “Toda a instituição” para valer em todas." : "" }}
              </p>
            </div>

            <div class="am-period-head">
              <h3 class="am-section-title">
                Período de envio
              </h3>
              <p class="am-card-subtitle">
                Em quais dias e horários esta mensagem será enviada.
              </p>
            </div>

            <div class="am-period">
              <div class="am-days">
                <button
                  v-for="day in weekDays"
                  :key="day.value"
                  type="button"
                  class="am-day"
                  :class="{ 'is-on': form.days.includes(day.value) }"
                  :aria-pressed="form.days.includes(day.value) ? 'true' : 'false'"
                  :title="day.long"
                  @click="toggleDay(day.value)"
                >
                  {{ day.short }}
                </button>
              </div>

              <div class="am-times">
                <input
                  v-model="form.start"
                  type="time"
                  class="am-time"
                  aria-label="Horário de início"
                >

                <i
                  class="fas fa-long-arrow-alt-right am-times-arrow"
                  aria-hidden="true"
                />

                <input
                  v-model="form.end"
                  type="time"
                  class="am-time"
                  aria-label="Horário de fim"
                >

                <label class="am-all-day">
                  <input
                    type="checkbox"
                    :checked="isAllDay"
                    @change="setAllDay($event.target.checked)"
                  >
                  Dia inteiro
                </label>
              </div>

              <p class="am-period-note">
                <i
                  class="fas"
                  :class="periodIcon(form)"
                  aria-hidden="true"
                />
                {{ periodNote }}
              </p>
            </div>

            <div class="am-period-head am-timezone-head">
              <h3 class="am-section-title">
                Fuso horário
              </h3>
              <p class="am-card-subtitle">
                Os horários dos períodos de envio seguem o fuso selecionado.
              </p>
            </div>

            <div class="am-timezone">
              <select
                v-model="form.timeZone"
                class="am-timezone-select"
                aria-label="Fuso horário"
              >
                <option
                  v-for="zone in timeZoneOptions"
                  :key="zone.id"
                  :value="zone.id"
                >
                  {{ zone.label }}
                </option>
              </select>
              <span
                class="am-timezone-caret"
                aria-hidden="true"
              />
            </div>

            <p class="am-timezone-now">
              <i
                class="far fa-clock"
                aria-hidden="true"
              />
              Agora: <strong>{{ timeZoneNow }}</strong>
            </p>
          </section>
        </div>

        <footer
          v-if="selectedKey !== null"
          class="am-footer"
        >
          <span
            class="am-status"
            :class="{ 'is-dirty': isDirty }"
          >
            <i
              class="fas"
              :class="isDirty ? 'fa-exclamation-circle' : 'fa-check-circle'"
              aria-hidden="true"
            />
            {{ statusLabel }}
          </span>

          <lila-button
            type="button"
            title="Salvar Configuração"
            :loading="saving"
            :is-disabled="saving"
            :second-button="{ type: 'button', title: 'Cancelar' }"
            @custom-click-event="submitForm"
            @custom-click-cancel-event="cancelEdit"
          />
        </footer>
      </div>

      <div
        v-if="messageToDelete"
        class="modal default-modal is-active"
        role="dialog"
        aria-modal="true"
      >
        <div
          class="modal-background"
          @click="messageToDelete = null"
        />

        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Excluir mensagem automática
            </p>
          </header>

          <section class="modal-card-body">
            <p class="am-modal-text">
              Tem certeza que deseja excluir
              <strong>“{{ messageToDelete.title }}”</strong>? Os pacientes
              deixam de receber esta resposta automática.
            </p>
          </section>

          <footer class="modal-card-foot">
            <lila-button
              type="button"
              title="Excluir"
              :loading="deleting"
              :is-disabled="deleting"
              :second-button="{ type: 'button', title: 'Cancelar' }"
              @custom-click-event="confirmDelete"
              @custom-click-cancel-event="messageToDelete = null"
            />
          </footer>
        </div>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";

import Input from "@components/shared/fields/Input.vue";
import Textarea from "@components/shared/fields/Textarea.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";

import { required, maxLength } from "@vuelidate/validators";

const WEEK_DAYS = [
  { value: "sun", short: "Dom", long: "Domingo" },
  { value: "mon", short: "Seg", long: "Segunda-feira" },
  { value: "tue", short: "Ter", long: "Terça-feira" },
  { value: "wed", short: "Qua", long: "Quarta-feira" },
  { value: "thu", short: "Qui", long: "Quinta-feira" },
  { value: "fri", short: "Sex", long: "Sexta-feira" },
  { value: "sat", short: "Sáb", long: "Sábado" }
];

const WEEK_DAY_ORDER = WEEK_DAYS.map((day) => day.value);

const ALL_DAY_START = "00:00";
const ALL_DAY_END = "23:59";

const DEFAULT_TIME_ZONE = "America/Sao_Paulo";

const TIME_ZONES = [
  { id: "America/Noronha", city: "Fernando de Noronha", offset: "GMT-2" },
  { id: "America/Sao_Paulo", city: "Brasília / São Paulo", offset: "GMT-3" },
  { id: "America/Bahia", city: "Salvador", offset: "GMT-3" },
  { id: "America/Fortaleza", city: "Fortaleza", offset: "GMT-3" },
  { id: "America/Recife", city: "Recife", offset: "GMT-3" },
  { id: "America/Maceio", city: "Maceió", offset: "GMT-3" },
  { id: "America/Araguaina", city: "Araguaína", offset: "GMT-3" },
  { id: "America/Belem", city: "Belém", offset: "GMT-3" },
  { id: "America/Santarem", city: "Santarém", offset: "GMT-3" },
  { id: "America/Manaus", city: "Manaus", offset: "GMT-4" },
  { id: "America/Cuiaba", city: "Cuiabá", offset: "GMT-4" },
  { id: "America/Campo_Grande", city: "Campo Grande", offset: "GMT-4" },
  { id: "America/Porto_Velho", city: "Porto Velho", offset: "GMT-4" },
  { id: "America/Boa_Vista", city: "Boa Vista", offset: "GMT-4" },
  { id: "America/Rio_Branco", city: "Rio Branco", offset: "GMT-5" },
  { id: "America/Eirunepe", city: "Eirunepé", offset: "GMT-5" }
].map((zone) => ({
  id: zone.id,
  label: `${zone.city} (${zone.offset}) · ${zone.id}`
}));

function emptyForm() {
  return {
    id: null,
    unitId: null,
    allInstitution: false,
    title: "",
    body: "",
    start: "19:00",
    end: "07:00",
    days: [],
    timeZone: DEFAULT_TIME_ZONE
  };
}

function formatNow(now, timeZone) {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone
    }).format(now);
  } catch (error) {
    return "--:--";
  }
}

function sortDays(days) {
  return [...days].sort(
    (a, b) => WEEK_DAY_ORDER.indexOf(a) - WEEK_DAY_ORDER.indexOf(b)
  );
}

function toTimeInput(value) {
  return typeof value === "string" ? value.slice(0, 5) : "";
}

function toApiTime(value) {
  return `${value}:00`;
}

export default {
  components: {
    "lila-input": Input,
    "lila-textarea": Textarea,
    "team-tree-picker": TeamTreePicker,
    "lila-button": Button,
    "lila-message": Message,
    "lila-default-template": DefaultTemplate
  },
  data() {
    return {
      weekDays: WEEK_DAYS,
      messages: [],
      units: [],
      canSelectInstitution: false,
      unitPickerOpen: false,
      selectedKey: null,
      form: emptyForm(),
      snapshot: "",
      messageToDelete: null,
      loadingContent: false,
      loadingUnits: false,
      saving: false,
      deleting: false,
      now: new Date(),
      clockTimer: null,
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  validations() {
    return {
      form: {
        title: { required, maxLength: maxLength(200) },
        body: { required }
      }
    };
  },
  computed: {
    unitTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.Id ?? node.id));
        if (!existing) {
          existing = { id: node.Id ?? node.id, name: node.Name ?? node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };

      this.units.forEach((unit) => {
        let children = roots;
        (Array.isArray(unit.Path) ? unit.Path : []).forEach((step) => {
          children = findOrAdd(children, step).children;
        });
        findOrAdd(children, unit);
      });

      return roots;
    },
    selectedUnitLabel() {
      if (this.form.allInstitution) return "Toda a instituição";

      if (!this.form.unitId) return "";

      const unit = this.units.find(
        (item) => String(item.Id) === String(this.form.unitId)
      );

      return unit ? this.unitFullPath(unit) : "";
    },
    unitPlaceholder() {
      if (this.loadingUnits) return "Carregando unidades…";

      return "Selecione a unidade";
    },
    isAllDay() {
      return this.form.start === ALL_DAY_START && this.form.end === ALL_DAY_END;
    },
    periodNote() {
      if (this.isAllDay) return "Dia inteiro";

      if (!this.form.start || !this.form.end) return "Defina o horário de envio.";

      return this.form.start > this.form.end
        ? "Período noturno · atravessa a madrugada"
        : "Período dentro do mesmo dia";
    },
    timeZoneOptions() {
      const zone = this.form.timeZone;

      if (!zone || TIME_ZONES.some((option) => option.id === zone)) {
        return TIME_ZONES;
      }

      return [...TIME_ZONES, { id: zone, label: zone }];
    },
    timeZoneNow() {
      return formatNow(this.now, this.form.timeZone || DEFAULT_TIME_ZONE);
    },
    isDirty() {
      return this.snapshot !== JSON.stringify(this.form);
    },
    statusLabel() {
      if (this.saving) return "Salvando…";
      if (this.isDirty) return "Há alterações não salvas";

      return "Todas as alterações foram salvas";
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.clockTimer = setInterval(() => {
      this.now = new Date();
    }, 15000);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    clearInterval(this.clockTimer);
  },
  created() {
    this.loadUnits();
    this.loadMessages();
  },
  methods: {
    handleError(error) {
      if (!error.response) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "Não foi possível falar com o servidor. Tente novamente."
        );
        return;
      }

      this.$Utilities.verifyToken(error.response.status);
      this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
    },
    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },
    unitFullPath(unit) {
      const path = Array.isArray(unit.Path) ? unit.Path : [];
      const names = path.map((step) => step.Name);

      return [...names, unit.Name].join(" › ");
    },
    pickUnit(id) {
      this.form.unitId = id === null ? null : String(id);
      this.form.allInstitution = false;
      if (id !== null) this.unitPickerOpen = false;
    },
    pickInstitution() {
      this.form.unitId = null;
      this.form.allInstitution = true;
      this.unitPickerOpen = false;
    },
    handleClickOutside(event) {
      if (!this.unitPickerOpen) return;

      const picker = this.$el.querySelector(".am-unit-picker-wrap");
      if (picker && !picker.contains(event.target)) this.unitPickerOpen = false;
    },
    unitName(unitId) {
      if (!unitId) return "";

      const unit = this.units.find((item) => String(item.Id) === String(unitId));

      return unit ? unit.Name : "";
    },
    daysSummary(days) {
      if (days.length === 0) return "Nenhum dia selecionado";
      if (days.length === 7) return "Todos os dias";

      const sorted = sortDays(days).join(",");

      if (sorted === "mon,tue,wed,thu,fri") return "Segunda a sexta";
      if (sorted === "sun,sat") return "Sábado e domingo";

      return sortDays(days)
        .map((value) => WEEK_DAYS.find((day) => day.value === value).short)
        .join(", ");
    },
    timeSummary(message) {
      if (message.start === ALL_DAY_START && message.end === ALL_DAY_END) {
        return "Dia inteiro";
      }

      return `${message.start} → ${message.end}`;
    },
    periodIcon(message) {
      if (message.start === ALL_DAY_START && message.end === ALL_DAY_END) {
        return "fa-sun";
      }

      return message.start > message.end ? "fa-moon" : "fa-clock";
    },
    async loadUnits() {
      this.loadingUnits = true;

      try {
        const response = await this.$axios.get("api/report/units", {
          headers: this.authHeader()
        });
        const data = response.data || {};

        this.canSelectInstitution = data.CanSelectInstitution === true;
        this.units = Array.isArray(data.Units) ? data.Units : [];
      } catch (error) {
        this.canSelectInstitution = false;
        this.units = [];
      } finally {
        this.loadingUnits = false;
      }
    },
    async loadMessages() {
      this.alert.show = false;
      this.loadingContent = true;

      try {
        const response = await this.$axios.get(
          "api/automaticmessage?page=1&limit=100",
          { headers: this.authHeader() }
        );
        const data = response.data || {};
        const list = Array.isArray(data.Data) ? data.Data : [];

        this.messages = list
          .map((item) => ({
            id: item.Id,
            unitId: item.UnitId,
            title: item.MessageTitle,
            body: item.MessageBody,
            start: toTimeInput(item.StartDateAndTime),
            end: toTimeInput(item.EndDateAndTime),
            days: (item.Days || "")
              .split(",")
              .map((day) => day.trim())
              .filter((day) => day.length > 0),
            timeZone: item.TimeZone || DEFAULT_TIME_ZONE
          }))
          .sort((a, b) => a.title.localeCompare(b.title));

        this.restoreSelection();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingContent = false;
      }
    },
    restoreSelection() {
      if (this.selectedKey === null) return;

      const current = this.messages.find(
        (message) => message.id === this.selectedKey
      );

      if (current) this.selectMessage(current);
      else this.clearSelection();
    },
    clearSelection() {
      this.selectedKey = null;
      this.form = emptyForm();
      this.snapshot = JSON.stringify(this.form);
      this.v.$reset();
    },
    selectMessage(message) {
      this.alert.show = false;
      this.selectedKey = message.id;
      this.form = {
        id: message.id,
        unitId: message.unitId === null ? null : String(message.unitId),
        allInstitution: message.unitId === null,
        title: message.title,
        body: message.body,
        start: message.start,
        end: message.end,
        days: sortDays(message.days),
        timeZone: message.timeZone
      };
      this.snapshot = JSON.stringify(this.form);
      this.v.$reset();
    },
    startNewMessage() {
      this.alert.show = false;
      this.selectedKey = "new";
      this.form = emptyForm();
      this.form.days = ["mon", "tue", "wed", "thu", "fri"];
      this.snapshot = JSON.stringify(this.form);
      this.v.$reset();
    },
    cancelEdit() {
      const current = this.messages.find(
        (message) => message.id === this.selectedKey
      );

      if (current) this.selectMessage(current);
      else this.clearSelection();
    },
    toggleDay(value) {
      this.form.days = this.form.days.includes(value)
        ? this.form.days.filter((day) => day !== value)
        : sortDays([...this.form.days, value]);
    },
    setAllDay(checked) {
      if (checked) {
        this.form.start = ALL_DAY_START;
        this.form.end = ALL_DAY_END;
        return;
      }

      this.form.start = "19:00";
      this.form.end = "07:00";
    },
    validateForm() {
      this.v.$touch();

      if (this.v.$invalid) return false;

      if (this.form.days.length === 0) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "Selecione ao menos um dia da semana."
        );
        return false;
      }

      if (!this.form.start || !this.form.end) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "Informe o horário de início e de fim."
        );
        return false;
      }

      if (this.form.start === this.form.end) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "O horário de início e de fim não podem ser iguais."
        );
        return false;
      }

      if (!this.form.allInstitution && !this.form.unitId) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "Selecione a unidade da mensagem."
        );
        return false;
      }

      return true;
    },
    async submitForm() {
      this.alert.show = false;

      if (!this.validateForm()) return;

      const payload = {
        UnitId: this.form.unitId ? Number(this.form.unitId) : null,
        MessageTitle: this.form.title,
        MessageBody: this.form.body,
        StartDateAndTime: toApiTime(this.form.start),
        EndDateAndTime: toApiTime(this.form.end),
        Days: sortDays(this.form.days).join(","),
        TimeZone: this.form.timeZone || DEFAULT_TIME_ZONE
      };

      this.saving = true;

      try {
        const isEdit = Boolean(this.form.id);

        const response = await this.$axios({
          method: isEdit ? "PUT" : "POST",
          url: isEdit
            ? `api/automaticmessage/${this.form.id}`
            : "api/automaticmessage",
          data: payload,
          headers: this.authHeader()
        });

        this.selectedKey = response.data.Id;

        await this.loadMessages();

        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          isEdit
            ? "Mensagem automática atualizada com sucesso."
            : "Mensagem automática criada com sucesso."
        );
      } catch (error) {
        this.handleError(error);
      } finally {
        this.saving = false;
      }
    },
    askDelete() {
      this.messageToDelete = this.messages.find(
        (message) => message.id === this.form.id
      );
    },
    async confirmDelete() {
      this.alert.show = false;
      this.deleting = true;

      try {
        await this.$axios({
          method: "DELETE",
          url: `api/automaticmessage/${this.messageToDelete.id}`,
          headers: this.authHeader()
        });

        this.messageToDelete = null;
        this.clearSelection();

        await this.loadMessages();

        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          "Mensagem automática excluída com sucesso."
        );
      } catch (error) {
        this.messageToDelete = null;
        this.handleError(error);
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style lang="scss">
.am-unit-picker-wrap {
  position: relative;
}

.am-unit-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 0 16px;
  font-size: 14px;
  color: #3a3a3a;
  background: #ffffff;
  border: 1px solid #e5e2ef;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.am-unit-select:disabled {
  cursor: default;
  opacity: 0.6;
}

.am-unit-select .is-placeholder {
  color: #9a9a9a;
}

.am-unit-caret {
  display: block;
  width: 0.5em;
  height: 0.5em;
  border: 2px solid #839097;
  border-top: 0;
  border-right: 0;
  border-radius: 2px;
  transform: rotate(-45deg);
  margin-top: -3px;
  flex: none;
}

.am-unit-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  z-index: 600;
}

.am-unit-picker-wrap .am-unit-picker .ttp {
  width: 100%;
  max-width: none;
}

$am-purple: #9a397d;
$am-dark: #2d2251;
$am-muted: #9b9aa8;
$am-border: #ebe9f2;

.automatic-messages {
  padding: 18px 32px 28px;

  h1.title {
    margin-top: 0;
    margin-bottom: 6px;
    font-size: 28px;
    line-height: 36px;
  }

  .field.input-component input,
  .textarea-component textarea,
  .select select {
    border-color: $am-border;
  }

  .field.input-component input,
  .select select {
    border-radius: 10px;
  }
}

.am-breadcrumb {
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 18px;
  color: $am-muted;

  strong {
    font-weight: 600;
    color: $am-dark;
  }
}

.am-breadcrumb-sep {
  margin: 0 6px;
}

.am-intro {
  max-width: 620px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 20px;
  color: $am-muted;
}

.am-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.am-card {
  padding: 20px;
  border: 1px solid $am-border;
  border-radius: 14px;
  background-color: #ffffff;
  box-shadow: 0 1px 4px 0 rgba(45, 34, 81, 0.08);
}

.am-card-head {
  margin-bottom: 16px;
}

.automatic-messages .am-card-title {
  font-size: 17px;
  line-height: 23px;
  font-weight: 700;
  color: $am-dark;
}

.am-card-subtitle {
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: $am-muted;
}

.automatic-messages .am-list-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin: 0;
  }
}

.am-list-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px 14px;
  cursor: pointer;
  border: 1px solid $am-border;
  border-left: 3px solid transparent;
  border-radius: 10px;
  text-align: left;
  background-color: #ffffff;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    border-color: rgba(154, 57, 125, 0.35);
  }

  &.is-selected {
    border-color: $am-border;
    border-left-color: $am-purple;
    background-color: #f4f2f8;
  }
}

.am-list-item-name {
  font-size: 14px;
  line-height: 19px;
  font-weight: 600;
  color: $am-dark;
}

.am-list-item-days {
  font-size: 12px;
  line-height: 17px;
  color: $am-purple;

  i {
    margin-right: 5px;
  }
}

.am-list-item-time,
.am-list-item-unit {
  font-size: 12px;
  line-height: 17px;
  color: $am-muted;
}

.am-list-empty {
  padding: 8px 0 4px;
  font-size: 13px;
  line-height: 18px;
  color: $am-muted;
}

.am-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 14px;
  padding: 11px 14px;
  cursor: pointer;
  border: 1px dashed rgba(154, 57, 125, 0.5);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: $am-purple;
  background-color: transparent;

  &:hover {
    background-color: #faf7fb;
  }
}

.am-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 260px;
  color: $am-muted;

  i {
    font-size: 28px;
  }

  p {
    font-size: 14px;
  }
}

.am-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.am-delete {
  padding: 4px 8px;
  cursor: pointer;
  border: none;
  font-size: 15px;
  color: $am-muted;
  background: none;

  &:hover {
    color: $am-purple;
  }
}

.am-field {
  margin-bottom: 22px;

  .field {
    margin-bottom: 0;
  }
}

.am-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: $am-dark;
}

.am-hint {
  margin-top: 6px;
  font-size: 12px;
  line-height: 17px;
  color: $am-muted;
}

.am-counter {
  margin-top: 6px;
  font-size: 12px;
  line-height: 17px;
  text-align: right;
  color: $am-muted;
}

.am-tip {
  display: flex;
  gap: 10px;
  margin-bottom: 22px;
  padding: 14px 16px;
  border-radius: 10px;
  background-color: #f1ecf7;

  i {
    margin-top: 2px;
    font-size: 15px;
    color: $am-purple;
  }

  p {
    font-size: 13px;
    line-height: 19px;
    color: $am-dark;
  }
}

.am-period-head {
  margin-bottom: 14px;
  padding-top: 18px;
  border-top: 1px solid $am-border;
}

.automatic-messages .am-section-title {
  font-size: 15px;
  line-height: 21px;
  font-weight: 700;
  color: $am-dark;
}

.am-period {
  padding: 16px;
  border: 1px solid $am-border;
  border-radius: 12px;
  background-color: #fdfcfe;
}

.am-days {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.am-day {
  min-width: 46px;
  padding: 7px 10px;
  cursor: pointer;
  border: 1px solid $am-border;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: $am-muted;
  background-color: #ffffff;
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;

  &:hover {
    border-color: rgba(154, 57, 125, 0.35);
  }

  &.is-on {
    border-color: $am-purple;
    color: #ffffff;
    background-color: $am-purple;
  }
}

.am-times {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.am-time {
  padding: 8px 12px;
  border: 1px solid $am-border;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  color: $am-dark;
  background-color: #ffffff;

  &:focus {
    outline: none;
    border-color: $am-purple;
  }
}

.am-times-arrow {
  color: $am-muted;
}

.am-all-day {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  cursor: pointer;
  font-size: 13px;
  color: $am-dark;
}

.am-period-note {
  margin-top: 12px;
  font-size: 12px;
  line-height: 17px;
  color: $am-muted;

  i {
    margin-right: 6px;
    color: $am-purple;
  }
}

.am-timezone-head {
  margin-top: 22px;
}

.am-timezone {
  position: relative;
}

.automatic-messages .am-timezone-select {
  width: 100%;
  min-height: 46px;
  padding: 0 48px 0 20px;
  cursor: pointer;
  appearance: none;
  border: 1px solid $am-border;
  border-radius: 24px;
  font-family: inherit;
  font-size: 15px;
  color: $am-dark;
  background-color: #ffffff;
  box-shadow: 0 2px 8px 0 rgba(45, 34, 81, 0.1);

  &:focus {
    outline: none;
    border-color: $am-purple;
  }
}

.am-timezone-caret {
  position: absolute;
  top: 50%;
  right: 22px;
  width: 0.5em;
  height: 0.5em;
  margin-top: -6px;
  pointer-events: none;
  border: 2px solid #839097;
  border-top: 0;
  border-right: 0;
  border-radius: 2px;
  transform: rotate(-45deg);
}

.am-timezone-now {
  margin-top: 10px;
  font-size: 12px;
  line-height: 17px;
  color: $am-muted;

  i {
    margin-right: 6px;
    color: $am-purple;
  }

  strong {
    color: $am-dark;
  }
}

.am-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 24px -32px 0;
  padding: 14px 32px;
  border-top: 1px solid $am-border;
  background-color: #ffffff;

  .button-component {
    margin: 0;
  }

  .control {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 12px;
  }

  .button {
    margin: 0;
  }
}

.am-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: $am-muted;

  i {
    color: #4caf50;
  }

  &.is-dirty i {
    color: #e0a92b;
  }
}

.am-modal-text {
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  color: #4a4a4a;
}

@media (max-width: 1023px) {
  .am-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
